// Spec §70 / §71: read-only detail page with the same actions as the
// mobile detail screen (Edit / Submit / Archive / Restore / Delete).
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { reportsApi, photosApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import { REPORT_STATUS_LABEL, REPORT_STATUS_TONE, formatDate } from '@/utils/format';
import type { ReportJson, ReportPhotoJson } from '@/api/types';
import { isVisiblePhoto, photoStatusLabel, photoUiStatusFor } from './photoStatusLabel';
import { PhotoViewer } from './PhotoViewer';

export function ReportDetailScreen() {
  const { id = '' } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportJson | null>(null);
  const [photos, setPhotos] = useState<ReportPhotoJson[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  /** The photo currently shown in the PhotoViewer (null = closed). */
  const [viewerPhoto, setViewerPhoto] = useState<ReportPhotoJson | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      setBusy(true);
      setError(null);
      try {
        const row = await reportsApi.get(getToken, id);
        if (!cancelled) setReport(row);
        const photoRows = await photosApi.list(getToken, id).catch(() => [] as ReportPhotoJson[]);
        if (!cancelled) {
          // Hide soft-deleted tombstones so the inline Photos strip
          // matches Mobile's `watchPhotos` filter. The backend returns
          // tombstones for reconciliation; we filter at the presentation
          // layer.
          setPhotos((photoRows ?? []).filter(isVisiblePhoto));
        }
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load the report.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, getToken]);

  // Resolve thumbnail URLs as object URLs that can be revoked on unmount.
  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    async function resolve() {
      const next: Record<string, string> = {};
      for (const photo of photos) {
        if (!photo.thumbnail_r2_key) continue;
        try {
          const url = await photosApi.fetchAsset(
            getToken,
            photosApi.thumbnailUrl(photo.report_id, photo.id),
          );
          if (cancelled) {
            URL.revokeObjectURL(url);
            continue;
          }
          next[photo.id] = url;
          created.push(url);
        } catch {
          // ignore — tile renders a placeholder
        }
      }
      if (!cancelled) setThumbnailUrls(next);
    }
    void resolve();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [photos, getToken]);

  if (busy) {
    return <div className="empty">Loading report…</div>;
  }
  if (!report) {
    return (
      <div className="empty">
        {error ? <div className="error-banner">{error}</div> : null}
        <Link to="/reports" className="btn">
          Back to reports
        </Link>
      </div>
    );
  }

  const title = report.title.trim() === '' ? '(Untitled)' : report.title;

  async function call(mutator: () => Promise<ReportJson>) {
    setActionBusy(true);
    setError(null);
    try {
      const updated = await mutator();
      setReport(updated);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Action failed.');
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <div className="stack">
      {/* Back navigation */}
      <button
        className="btn btn--ghost"
        type="button"
        onClick={() => navigate('/workspace')}
        style={{ alignSelf: 'flex-start', padding: '6px 0', border: 'none', fontSize: 14 }}
      >
        ← Back to Workspace
      </button>

      <div className="card detail-action-bar">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0 }}>{title}</h2>
            <div className="muted" style={{ fontSize: 13 }}>
              Created {formatDate(report.created_at)} · Updated {formatDate(report.updated_at)} · v
              {report.version}
            </div>
          </div>
          <span className={REPORT_STATUS_TONE[report.status]}>
            {REPORT_STATUS_LABEL[report.status]}
          </span>
        </div>
        {error ? <div className="error-banner">{error}</div> : null}
        <p style={{ whiteSpace: 'pre-wrap' }}>{report.content || '(No content yet.)'}</p>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {/*
            Mobile lifecycle rule (commit 6343a80): Edit is only
            available on Draft reports. Submitted / Archived reports
            are read-only — keep Submit / Archive / Restore / Delete
            unchanged and let Photos stay accessible via the photos
            grid below.
          */}
          {report.status === 'draft' ? (
            <button
              className="btn btn--primary"
              disabled={actionBusy}
              onClick={() => navigate(`/reports/${report.id}/edit`)}
            >
              Edit
            </button>
          ) : null}
          {report.status === 'draft' ? (
            <button
              className="btn"
              disabled={actionBusy}
              onClick={() => call(() => reportsApi.submit(getToken, report.id))}
            >
              Submit
            </button>
          ) : null}
          {/* Archive / Restore / Delete in a compact group */}
          <div className="row" style={{ gap: 6, marginLeft: 'auto' }}>
            {report.status === 'archived' ? (
              <button
                className="btn"
                disabled={actionBusy}
                onClick={() => call(() => reportsApi.restore(getToken, report.id))}
              >
                Restore
              </button>
            ) : (
              <button
                className="btn"
                disabled={actionBusy}
                onClick={() => call(() => reportsApi.archive(getToken, report.id))}
              >
                Archive
              </button>
            )}
            <button
              className="btn btn--danger"
              disabled={actionBusy}
              onClick={async () => {
                if (!window.confirm('Delete this report? This will remove it from your workspace.'))
                  return;
                setActionBusy(true);
                try {
                  await reportsApi.deleteReport(getToken, report.id);
                  navigate('/workspace', { replace: true });
                } catch (e) {
                  const err = e instanceof ApiException ? e : null;
                  setError(err?.message ?? 'Could not delete the report.');
                } finally {
                  setActionBusy(false);
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Photos ({photos.length})</h3>
        {photos.length === 0 ? (
          <div className="empty">
            No photos attached. Use the mobile app to add photos from your device.
          </div>
        ) : (
          <div className="photo-grid">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="photo-tile photo-tile--clickable"
                onClick={() => setViewerPhoto(photo)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setViewerPhoto(photo);
                }}
                aria-label="View original photo"
              >
                {thumbnailUrls[photo.id] ? (
                  <img src={thumbnailUrls[photo.id]} alt="Report photo" />
                ) : (
                  <div className="empty" style={{ padding: 8, fontSize: 12 }}>
                    {photoStatusLabel(photo)}
                  </div>
                )}
                <div
                  className="photo-tile__caption"
                  data-status={photoUiStatusFor(photo.upload_status)}
                >
                  {photoStatusLabel(photo)}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewerPhoto && (
          <PhotoViewer
            photo={viewerPhoto}
            getToken={getToken}
            onClose={() => setViewerPhoto(null)}
          />
        )}
      </div>
    </div>
  );
}
